/**
 * Marking the high-frequency words inside a phrase.
 *
 * The whole point of the refresher is seeing the commonest words *at work*
 * rather than in a column, so they have to be visible in the sentence itself.
 *
 * Longest match wins, which is not optional. 什么 contains both 什 and 么;
 * 我们 contains 我; 时候 contains 时. A naive scan would highlight 我 inside
 * 我们 and leave 们 bare, splitting a word in half on screen and teaching the
 * reader something false about where the boundaries are.
 */

export interface Segment {
  text: string
  /** True when this run of characters is one of the tracked words. */
  marked: boolean
}

/**
 * Split a phrase into marked and unmarked runs.
 *
 * Adjacent marked words stay separate segments rather than merging, so each one
 * can carry its own title/tooltip — 我们都 is three words, not one blob.
 */
export function markWords(phrase: string, words: readonly string[]): Segment[] {
  if (!phrase) return []

  // Longest first, so a scan at any position takes the biggest word that fits.
  const byLength = [...new Set(words.filter(Boolean))].sort((a, b) => b.length - a.length)
  const longest = byLength.reduce((max, w) => Math.max(max, w.length), 0)

  const out: Segment[] = []
  let plain = ''
  let i = 0

  while (i < phrase.length) {
    let hit: string | null = null
    // Try the longest possible window at this position and shrink.
    for (let len = Math.min(longest, phrase.length - i); len > 0 && !hit; len--) {
      const window = phrase.slice(i, i + len)
      if (byLength.includes(window)) hit = window
    }

    if (hit) {
      if (plain) {
        out.push({ text: plain, marked: false })
        plain = ''
      }
      out.push({ text: hit, marked: true })
      i += hit.length
    } else {
      plain += phrase[i]
      i += 1
    }
  }

  if (plain) out.push({ text: plain, marked: false })
  return out
}
